/**
 * Runtime icon-contrast pass (dark theme only).
 *
 * WHY THIS EXISTS
 * CSS cannot tell a black LINE icon (needs inverting) from a FILLED icon whose
 * artwork already contains its own light glyph (must NOT be inverted), nor from
 * a colour logo (must never be touched). Every selector-based attempt got one
 * of the three wrong:
 *   - `brightness(0) invert()` flattens a filled icon's internal shape first,
 *     turning edit/delete buttons into solid white squares.
 *   - Broad selectors like `button > img` catch colour artwork.
 *   - Narrow selectors miss whole pages, so icons stay invisible.
 *
 * So instead of guessing from selectors, this samples the actual pixels once
 * per image and tags only the ones that genuinely disappear on a dark surface.
 * `dark.css` then styles by that tag alone.
 *
 * Tags applied to <img>:
 *   data-qv-icon="dark"  monochrome DARK artwork -> invert to light
 *   data-qv-icon="flat"  dark artwork that also contains light pixels
 *                        (a filled icon) -> invert WITHOUT flattening
 * Anything light, colourful, large, or unreadable (cross-origin) is left alone.
 *
 * Runs only while data-theme="dark" is present, is idempotent per element, and
 * re-runs on DOM changes (debounced) so lazily rendered rows are covered.
 */

const MAX_ICON_PX = 64; // larger than this is imagery, not an icon
const PROCESSED = "data-qv-icon-checked";

// One canvas for every sample. Creating one per icon, on a page full of
// glyphs, was a large part of the multi-second freeze on theme switch.
const sampleCanvas = document.createElement("canvas");
sampleCanvas.width = 16;
sampleCanvas.height = 16;
const sampleCtx = sampleCanvas.getContext("2d", { willReadFrequently: true });

function sample(img) {
  try {
    if (!img.complete || !img.naturalWidth || !sampleCtx) return null;
    sampleCtx.clearRect(0, 0, 16, 16);
    sampleCtx.drawImage(img, 0, 0, 16, 16);
    const d = sampleCtx.getImageData(0, 0, 16, 16).data;

    let sum = 0, count = 0, light = 0, chroma = 0;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 40) continue; // ignore transparent
      const r = d[i], g = d[i + 1], b = d[i + 2];
      const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      sum += lum;
      count++;
      if (lum > 0.6) light++;
      chroma += Math.max(r, g, b) - Math.min(r, g, b);
    }
    // A thin glyph (a "+" or a 1px outline) legitimately covers very few
    // pixels. Requiring 24 rejected those as unjudgeable and left them
    // untouched — which is exactly how `add-icon` stayed invisible. Readiness
    // is handled by the complete/naturalWidth check above, so this only needs
    // to reject a genuinely empty sample.
    if (count < 8) return null;
    return {
      mean: sum / count,
      lightRatio: light / count,
      chroma: chroma / count,
    };
  } catch (e) {
    return null; // tainted canvas — never guess
  }
}

function classify(img) {
  const s = sample(img);
  if (!s) return null;

  // Light enough to read on a dark surface already.
  if (s.mean > 0.34) return null;

  // COLOURFUL and dark. "Leave colourful artwork alone" was too blunt: these
  // icons measured chroma 64-73 with mean 0.10 — genuinely colourful AND
  // genuinely invisible. Flipping luminance while rotating hue back keeps
  // them recognisably themselves instead of flattening them to white.
  if (s.chroma > 42) return "tinted";

  // Monochrome and dark. If it also carries light pixels it is a FILLED icon
  // whose glyph brightness(0) would destroy; invert without flattening.
  return s.lightRatio > 0.12 ? "flat" : "dark";
}

let scanQueue = [];
let scanPumping = false;

function pumpScan(deadline) {
  const start = performance.now();
  const budget = deadline && typeof deadline.timeRemaining === "function"
    ? Math.min(8, Math.max(4, deadline.timeRemaining()))
    : 6;
  const slice = [];
  while (scanQueue.length && performance.now() - start < budget && slice.length < 16) {
    const img = scanQueue.pop();
    if (img && img.isConnected && !img.hasAttribute(PROCESSED)) slice.push(img);
  }
  // Measure the slice, then write. Mixing the two forces a layout per icon.
  const measured = slice.map((img) => ({ img, box: img.getBoundingClientRect() }));
  measured.forEach(({ img, box }) => {
    if (!img.isConnected || img.hasAttribute(PROCESSED)) return;
    if (!box.width || !box.height) return;
    if (box.width > MAX_ICON_PX || box.height > MAX_ICON_PX) {
      img.setAttribute(PROCESSED, "skip");
      return;
    }
    if (
      img.closest(".ask-quickvee-toolbar-btn") ||
      img.getAttribute("data-qv-logo") === "ask-quickvee"
    ) {
      img.setAttribute(PROCESSED, "logo");
      img.removeAttribute("data-qv-icon");
      return;
    }
    if (!img.complete || !img.naturalWidth) return;
    const verdict = classify(img) || "ok";
    img.setAttribute(PROCESSED, verdict);
    if (verdict === "dark" || verdict === "flat" || verdict === "tinted") {
      img.setAttribute("data-qv-icon", verdict);
    }
  });

  if (scanQueue.length) {
    const next = window.requestIdleCallback
      ? (cb) => requestIdleCallback(cb, { timeout: 400 })
      : (cb) => setTimeout(() => cb(), 32);
    next(pumpScan);
    return;
  }
  scanPumping = false;
}

function beginScan() {
  if (document.documentElement.getAttribute("data-theme") !== "dark") return;
  const imgs = document.querySelectorAll("img:not([" + PROCESSED + "])");
  if (!imgs.length) return;
  scanQueue = Array.from(imgs);
  if (scanPumping) return;
  scanPumping = true;
  pumpScan();
}

let scheduled = false;
function schedule() {
  if (scheduled) return;
  scheduled = true;
  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    scheduled = false;
    beginScan();
  };
  // Paint the theme first. Measuring icons in the same frame forces a layout
  // of the whole dirty stylesheet and is what locked the tab for seconds.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (typeof requestIdleCallback === "function") {
        requestIdleCallback(start, { timeout: 800 });
      } else {
        setTimeout(start, 32);
      }
    });
  });
  // rAF and idle callbacks do not run in a background tab.
  setTimeout(start, 900);
}

let started = false;
export function startIconContrast() {
  if (started || typeof window === "undefined") return;
  started = true;

  // Images decode asynchronously; a load event means a previously
  // unjudgeable image can now be sampled.
  window.addEventListener("load", schedule, { once: true });
  document.addEventListener("load", (e) => {
    if (e.target && e.target.tagName === "IMG") {
      e.target.removeAttribute(PROCESSED);
      schedule();
    }
  }, true);

  const mo = new MutationObserver(schedule);
  mo.observe(document.documentElement, { childList: true, subtree: true });

  // Entering dark needs one pass over icons that were never tagged (the page
  // loaded in light, so earlier scans bailed out). Do not clear existing tags:
  // they describe pixels, not the theme, and wiping them forced a second
  // full-page sample in the same turn as the style recalc.
  const themeObserver = new MutationObserver(schedule);
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });

  // Belt-and-braces re-scans: images that were cached (so fired no load event)
  // and not yet decoded during the first pass get another chance.
  [300, 1200, 3000].forEach((ms) => setTimeout(schedule, ms));

  schedule();
}
